import React, { useState } from "react";
import {
  Modal,
  Button,
  Form,
  Space,
  Result,
  Select,
  Input,
  Upload,
  message,
} from "antd";
import { InboxOutlined } from "@ant-design/icons";
import type { SelectProps, UploadFile } from "antd";

const { Dragger } = Upload;

const documentTypes: SelectProps["options"] = [
  { label: "Death Certificate", value: "Death Certificate" },
  { label: "ID Document of Deceased", value: "ID Document of Deceased" },
  { label: "BI-1663 Certificate", value: "BI-1663 Certificate" },
  { label: "Marriage Certificate", value: "Marriage Certificate" },
  { label: "ID Document of Spouse", value: "ID Document of Spouse" },
  {
    label: "ID/Birth Certificate of Dependant",
    value: "ID/Birth Certificate of Dependant",
  },
  { label: "Other Document", value: "Other Document" },
];

interface IModalProps {
  show: boolean;
  showModal: Function;
  handleAdd: (docs: { Name: string; Value: string }[]) => Promise<any>;
}

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const buildDocName = (
  baseName: string,
  customName: string,
  index: number,
  total: number
) => {
  const label = customName || baseName;
  if (total <= 1) return label;
  return index === 0 ? label : `${label}_${index + 1}`;
};

const AddClaimDocumentModal: React.FC<IModalProps> = ({
  show,
  showModal,
  handleAdd,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [phase, setPhase] = useState<"form" | "success" | "error">("form");
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const handleModal = () => {
    showModal();
    form.resetFields();
    setFileList([]);
    setErrorMsg("");
    setPhase("form");
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (!fileList.length) {
        message.error("Please select at least one file.");
        return;
      }

      setLoading(true);
      const baseName: string = values.documentType;
      const customName: string = (values.name || "").trim();

      const docs: { Name: string; Value: string }[] = [];
      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i].originFileObj as File | undefined;
        if (!file) continue;
        const base64 = await fileToBase64(file);
        docs.push({
          Name: buildDocName(baseName, customName, i, fileList.length),
          Value: base64,
        });
      }

      if (!docs.length) {
        message.error("Could not read selected files.");
        setLoading(false);
        return;
      }

      const response = await handleAdd(docs);
      if (response?.success === false) {
        setErrorMsg(response?.message || "Unable to upload documents.");
        setPhase("error");
      } else {
        setPhase("success");
      }
    } catch (err: any) {
      if (err?.errorFields) return;
      setErrorMsg(err?.message || "Unable to upload documents.");
      setPhase("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Upload Claim Documents"
      open={show}
      onOk={handleModal}
      onCancel={handleModal}
      width={640}
      footer={null}
      destroyOnClose
    >
      {phase === "form" && (
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="documentType"
            label="Document Type"
            rules={[{ required: true, message: "Select a document type" }]}
          >
            <Select
              size="large"
              options={documentTypes}
              placeholder="Document Type"
            />
          </Form.Item>
          <Form.Item name="name" label="Name (optional override)">
            <Input size="large" placeholder="Defaults to document type" />
          </Form.Item>
          <Form.Item label="Files" required>
            <Dragger
              multiple
              accept=".pdf,.jpg,.jpeg,.png"
              fileList={fileList}
              beforeUpload={() => false}
              onChange={({ fileList: next }) => setFileList(next)}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                Click or drag files to upload (multiple allowed)
              </p>
              <p className="ant-upload-hint">PDF, JPG, PNG</p>
            </Dragger>
          </Form.Item>
          <Space direction="vertical" style={{ width: "100%" }}>
            <Button
              type="primary"
              block
              size="large"
              loading={loading}
              onClick={onSubmit}
            >
              Upload
            </Button>
            <Button block size="large" disabled={loading} onClick={handleModal}>
              Cancel
            </Button>
          </Space>
        </Form>
      )}

      {phase === "success" && (
        <Result status="success" title="Documents uploaded">
          <Space direction="vertical" style={{ width: "100%" }}>
            <Button block size="large" onClick={handleModal}>
              Okay
            </Button>
          </Space>
        </Result>
      )}

      {phase === "error" && (
        <Result status="error" title={errorMsg || "Upload failed"}>
          <Space direction="vertical" style={{ width: "100%" }}>
            <Button block size="large" onClick={() => setPhase("form")}>
              Try again
            </Button>
            <Button block size="large" onClick={handleModal}>
              Close
            </Button>
          </Space>
        </Result>
      )}
    </Modal>
  );
};

export default AddClaimDocumentModal;
